import axios from "axios";
import { useEffect, useState } from "react";
import { Star, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "website_rating", label: "Website experience", testid: "website" },
  { key: "product_rating", label: "Product quality", testid: "product" },
  { key: "order_rating", label: "Order experience", testid: "order" },
];

const StarRow = ({ label, value, onChange, testid }) => (
  <div className="star-row" data-testid={`rating-${testid}-row`}>
    <span>{label}</span>
    <div className="stars">
      {[1, 2, 3, 4, 5].map(n => (
        <button type="button" key={n} className={n <= value ? "star active" : "star"} onClick={() => onChange(n)} data-testid={`rating-${testid}-star-${n}`} aria-label={`${label}: ${n} star`}>
          <Star size={20} fill={n <= value ? "currentColor" : "none"}/>
        </button>
      ))}
    </div>
  </div>
);

export const FeedbackSection = ({ user }) => {
  const [ratings, setRatings] = useState({ website_rating: 0, product_rating: 0, order_rating: 0 });
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    axios.get(`${API}/feedbacks/summary`).then(res => setSummary(res.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (Object.values(ratings).some(v => v === 0)) {
      toast.error("Please rate all three", { description: "Tap the stars for website, product and order experience." });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/feedbacks`, { name: user?.name || "", ...ratings, comment });
      toast.success("Thank you for the feedback", { description: "It helps us grow better." });
      setRatings({ website_rating: 0, product_rating: 0, order_rating: 0 });
      setComment("");
      const { data } = await axios.get(`${API}/feedbacks/summary`);
      setSummary(data);
    } catch {
      toast.error("Something went wrong", { description: "Please try again in a moment." });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="feedback shell" id="feedback">
      <Reveal className="section-heading">
        <span className="eyebrow">Your words matter</span>
        <h2>Tell us how<br/><em>we did.</em></h2>
        {summary && summary.count > 0 && (
          <p data-testid="feedback-summary">{summary.count} review{summary.count === 1 ? "" : "s"} so far · Product {summary.product_avg}/5 · Website {summary.website_avg}/5 · Ordering {summary.order_avg}/5</p>
        )}
      </Reveal>
      <Reveal delay={0.1}>
        <form className="feedback-card" onSubmit={submit} data-testid="feedback-form">
          {CATEGORIES.map(c => (
            <StarRow key={c.key} label={c.label} value={ratings[c.key]} onChange={n => setRatings({ ...ratings, [c.key]: n })} testid={c.testid}/>
          ))}
          <textarea placeholder="Anything you'd like to share? (optional)" value={comment} onChange={e => setComment(e.target.value)} data-testid="feedback-comment-input"/>
          <button className="button button-primary" disabled={sending} data-testid="feedback-submit-button">{sending ? "Sending…" : "Share feedback"} <Send size={15}/></button>
        </form>
      </Reveal>
    </section>
  );
};
